import { jsonConstants } from '@thoughtspot/json-constants';
import { getAllGroupGUID, getGuidsToSkip, isOrgUiEnabled, } from '@thoughtspot/session-service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PrincipalVisibilityType } from "../../../../../../services/generated/graphql-types";
import { useGetUserAccessibleOrgsQuery, useGetUserAndGroupsQuery, useGetUserDetailsByIdWithUsersListLazyQuery, useUpdateUserMutation, } from "../../../../../../services/user-group/user-group-service";
import { hasCreateEditInputError, } from '../../../../../user-management/users/util';
import { captureFailureEvent, CHECKBOX_SECTION_PAGINATION_BATCHSIZE, } from '../../../../../user-management/util';
import { classifyServerError } from '../../../server-errors';
import { useAdminUserTracking } from '../../../shared';
import { buildUpdateContent, hasUserDataChanged, SubmitResult } from './utils';
const INITIAL_ERRORS = {
    isNameRequired: false,
    isDisplayNameRequired: false,
    isNameLong: false,
    isDisplayNameLong: false,
    isNotWhitelistedDomainEmail: false,
    isEmailEmpty: false,
    arePasswordsNotMatching: false,
    isPasswordEmpty: false,
    isConfirmPasswordEmpty: false,
    isPasswordValidationRuleFailed: false,
    isNameEmailNotSame: false,
};
const VALIDATE_ALL_FIELDS = {
    isNameUntouched: false,
    isDisplayNameUntouched: false,
    isPasswordUntouched: false,
    isConfirmPasswordUntouched: false,
    isEmailUntouched: false,
};
const FIELD_ERROR_MAP = {
    email: ['isEmailEmpty', 'isNotWhitelistedDomainEmail'],
    name: ['isNameRequired', 'isNameLong'],
    displayName: ['isDisplayNameRequired', 'isDisplayNameLong'],
    password: [
        'isPasswordEmpty',
        'isPasswordValidationRuleFailed',
        'arePasswordsNotMatching',
    ],
    confirmPassword: ['isConfirmPasswordEmpty', 'arePasswordsNotMatching'],
};
const BLOCKING_ERROR_KEYS = Array.from(new Set(Object.values(FIELD_ERROR_MAP).flat()));
const parseUserDetails = (raw) => {
    const details = raw.getUserDetailById?.[0];
    if (!details)
        return null;
    return {
        email: details.userContent?.userProperties?.mail ?? '',
        name: details.header?.name ?? '',
        displayName: details.displayName ?? details.header?.displayName ?? '',
        allowOthersToShare: details.visibility !== PrincipalVisibilityType.NonSharable,
        groupIds: new Set((raw.listUserGroupById ?? [])
            .map((group) => group?.id)
            .filter((id) => Boolean(id) && id !== getAllGroupGUID())),
        orgIds: new Set((details.header?.orgIds ?? []).map(String)),
    };
};
export const useEditUserForm = ({ userId, isClusterAdmin, }) => {
    const tracking = useAdminUserTracking();
    const [form, setForm] = useState({
        email: '',
        name: '',
        displayName: '',
        password: '',
        confirmPassword: '',
        allowOthersToShare: true,
        sendWelcomeEmail: false,
        welcomeEmailMessage: '',
    });
    const [errors, setErrors] = useState({ ...INITIAL_ERRORS });
    const [serverFieldErrors, setServerFieldErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [originalValues, setOriginalValues] = useState(null);
    const [belongToAllOrgs, setBelongToAllOrgs] = useState(false);
    const [isFederated, setIsFederated] = useState(false);
    const [isSystemPrincipal, setIsSystemPrincipal] = useState(false);
    const [isSuperUser, setIsSuperUser] = useState(false);
    const { data: raw, error: fetchError, getUserById, } = useGetUserDetailsByIdWithUsersListLazyQuery([userId], userId, isClusterAdmin ? '-1' : '');
    useEffect(() => {
        if (userId)
            getUserById();
    }, [userId]);
    useEffect(() => {
        if (!raw || isDataLoaded)
            return;
        const parsedDetails = parseUserDetails(raw);
        if (!parsedDetails)
            return;
        const { email, name, displayName, allowOthersToShare } = parsedDetails;
        setForm(prev => ({
            ...prev,
            email,
            name,
            displayName,
            allowOthersToShare,
        }));
        setOriginalValues(parsedDetails);
        const details = raw?.getUserDetailById?.[0];
        setBelongToAllOrgs(!!details?.header?.belongToAllOrgs);
        setIsFederated(details?.userContent?.oidcUserType === jsonConstants.FEDERATED_USER);
        setIsSystemPrincipal(!!details?.isSystemPrincipal);
        setIsSuperUser(!!details?.isSuperUser);
        setIsDataLoaded(true);
    }, [raw, isDataLoaded]);
    const [groupSearchText, setGroupSearchText] = useState('');
    const { data: userAndGroupsData, loading: groupsLoading, } = useGetUserAndGroupsQuery(groupSearchText, getGuidsToSkip(), CHECKBOX_SECTION_PAGINATION_BATCHSIZE);
    const orgsQueryEnabled = isClusterAdmin && isOrgUiEnabled();
    const { data: accessibleOrgsData, loading: orgsLoading, } = useGetUserAccessibleOrgsQuery(!orgsQueryEnabled);
    const { updateUser } = useUpdateUserMutation();
    const groupsList = userAndGroupsData?.getUserGroupsPrincipalList?.objects || [];
    const orgsList = orgsQueryEnabled
        ? accessibleOrgsData?.Data?.orgs || []
        : [];
    const isLoading = !isDataLoaded && !fetchError;
    const trimmedForm = useCallback(() => ({
        name: form.name.trim(),
        displayName: form.displayName.trim(),
        email: form.email.trim(),
        allowOthersToShare: form.allowOthersToShare,
    }), [form]);
    const updateField = useCallback((key, value) => {
        const updatedForm = { ...form, [key]: value };
        setForm(updatedForm);
        const errorKeysForField = FIELD_ERROR_MAP[key];
        if (errorKeysForField) {
            const revalidated = hasCreateEditInputError({ ...INITIAL_ERRORS }, {
                name: updatedForm.name.trim(),
                displayName: updatedForm.displayName.trim(),
                email: updatedForm.email.trim(),
                password: updatedForm.password,
                confirmPassword: updatedForm.confirmPassword,
                allowOthersToShare: updatedForm.allowOthersToShare,
            }, true, VALIDATE_ALL_FIELDS);
            const fieldErrors = {};
            errorKeysForField.forEach(k => {
                fieldErrors[k] = revalidated[k];
            });
            setErrors(prev => ({ ...prev, ...fieldErrors }));
        }
        if (key === 'name' && serverFieldErrors.name) {
            setServerFieldErrors(prev => ({ ...prev, name: undefined }));
        }
        setSubmitError(null);
    }, [form, serverFieldErrors.name]);
    const actions = useMemo(() => ({
        setEmail: (value) => updateField('email', value),
        setName: (value) => updateField('name', value),
        setDisplayName: (value) => updateField('displayName', value),
        setAllowOthersToShare: (value) => updateField('allowOthersToShare', value),
        setPassword: (value) => updateField('password', value),
        setConfirmPassword: (value) => updateField('confirmPassword', value),
    }), [updateField]);
    const validateCredentials = useCallback(() => {
        const result = hasCreateEditInputError({ ...INITIAL_ERRORS }, {
            ...trimmedForm(),
            password: form.password,
            confirmPassword: form.confirmPassword,
        }, true, VALIDATE_ALL_FIELDS);
        setErrors(result);
        return !BLOCKING_ERROR_KEYS.some(k => result[k]);
    }, [trimmedForm, form.password, form.confirmPassword]);
    const hasChanges = useCallback((selectedGroups, selectedOrgs) => {
        if (!originalValues)
            return true;
        if (form.password)
            return true;
        const currentValues = trimmedForm();
        return hasUserDataChanged({
            ...currentValues,
            groupIds: selectedGroups,
            orgIds: selectedOrgs,
        }, originalValues, isClusterAdmin);
    }, [trimmedForm, originalValues, isClusterAdmin, form.password]);
    const handleSubmit = useCallback(async (selectedGroupIds, selectedOrgIds) => {
        if (!hasChanges(selectedGroupIds, selectedOrgIds))
            return SubmitResult.NO_CHANGES;
        setIsSubmitting(true);
        setSubmitError(null);
        setServerFieldErrors({});
        const fieldsChanged = [];
        let groupsAdded = 0;
        let groupsRemoved = 0;
        let orgsAdded = 0;
        let orgsRemoved = 0;
        if (originalValues) {
            if (originalValues.name !== form.name.trim())
                fieldsChanged.push('name');
            if (originalValues.displayName !== form.displayName.trim())
                fieldsChanged.push('displayName');
            if (originalValues.email !== form.email.trim())
                fieldsChanged.push('email');
            if (originalValues.allowOthersToShare !==
                form.allowOthersToShare)
                fieldsChanged.push('allowOthersToShare');
            const origGroups = originalValues.groupIds;
            groupsAdded = [...selectedGroupIds].filter(id => !origGroups.has(id)).length;
            groupsRemoved = [...origGroups].filter(id => !selectedGroupIds.has(id)).length;
            if (groupsAdded || groupsRemoved)
                fieldsChanged.push('groups');
            const origOrgs = originalValues.orgIds;
            orgsAdded = [...selectedOrgIds].filter(id => !origOrgs.has(id))
                .length;
            orgsRemoved = [...origOrgs].filter(id => !selectedOrgIds.has(id)).length;
            if (orgsAdded || orgsRemoved)
                fieldsChanged.push('orgs');
        }
        try {
            const details = raw?.getUserDetailById?.[0];
            if (!details)
                return SubmitResult.ERROR;
            const content = buildUpdateContent(details, form, selectedGroupIds, selectedOrgIds, isClusterAdmin);
            await updateUser({
                userid: userId,
                content,
                password: form.password,
                isAllOrgContext: isClusterAdmin,
                triggeredbyadmin: true,
            });
            tracking.editSaved({
                targetUserId: userId,
                fieldsChanged,
                changeCount: fieldsChanged.length,
            });
            if (groupsAdded) {
                tracking.groupAssigned({
                    targetUserId: userId,
                    groupCount: groupsAdded,
                });
            }
            if (groupsRemoved) {
                tracking.groupUnassigned({
                    targetUserId: userId,
                    groupCount: groupsRemoved,
                });
            }
            if (orgsAdded) {
                tracking.orgAssigned({
                    targetUserId: userId,
                    orgCount: orgsAdded,
                    assignmentSource: 'detail_pane',
                });
            }
            if (orgsRemoved) {
                tracking.orgUnassigned({
                    targetUserId: userId,
                    orgCount: orgsRemoved,
                });
            }
            return SubmitResult.SUCCESS;
        }
        catch (err) {
            const classified = classifyServerError(err);
            if (classified.isDuplicateName) {
                setServerFieldErrors({ name: classified.message });
            }
            else {
                setSubmitError(classified.message);
            }
            captureFailureEvent('EDIT_USER', classified.rawErrorObj, classified.message);
            tracking.editFailed({
                targetUserId: userId,
                errorCode: classified.code,
                errorMessage: classified.message,
            });
            return classified.isDuplicateName
                ? SubmitResult.DUPLICATE_NAME
                : SubmitResult.ERROR;
        }
        finally {
            setIsSubmitting(false);
        }
    }, [
        form,
        userId,
        isClusterAdmin,
        updateUser,
        hasChanges,
        raw,
        tracking,
        originalValues,
    ]);
    return {
        form,
        actions,
        errors,
        serverFieldErrors,
        setServerFieldErrors,
        isLoading,
        isSubmitting,
        submitError,
        validateCredentials,
        handleSubmit,
        originalValues,
        belongToAllOrgs,
        isFederated,
        isSystemPrincipal,
        isSuperUser,
        groupsData: {
            list: groupsList,
            loading: groupsLoading,
            searchText: groupSearchText,
            setSearchText: setGroupSearchText,
        },
        orgsData: {
            list: orgsList,
            loading: orgsLoading,
        },
    };
};
//# sourceMappingURL=useEditUserForm.js.map